import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import styles from './testimonials.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, type Testimonial, type TestimonialStatus } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/testimonials')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: TestimonialsPage,
})

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

const STATUS_VARIANT: Record<TestimonialStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

function StarRating({ rating }: { rating: number | null }) {
  if (rating === null) return <span className={styles.dateCell}>--</span>
  return (
    <span className={styles.stars}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? undefined : styles.starEmpty}>
          &#9733;
        </span>
      ))}
    </span>
  )
}

function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [favoriteOnly, setFavoriteOnly] = useState(false)

  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 10

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchTestimonials = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const params: {
        page: number
        limit: number
        status?: TestimonialStatus
        search?: string
        favoriteOnly?: boolean
      } = {
        page,
        limit,
      }
      if (statusFilter) params.status = statusFilter as TestimonialStatus
      if (searchDebounced) params.search = searchDebounced
      if (favoriteOnly) params.favoriteOnly = true

      const response = await api.listTestimonials(params)
      setTestimonials(response.data)
      setTotalPages(response.pagination.totalPages)
      setTotal(response.pagination.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load testimonials')
    } finally {
      setIsLoading(false)
    }
  }, [page, statusFilter, searchDebounced, favoriteOnly])

  useEffect(() => {
    void fetchTestimonials()
  }, [fetchTestimonials])

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [statusFilter, searchDebounced, favoriteOnly])

  const handleSelectAll = () => {
    if (selected.size === testimonials.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(testimonials.map((t) => t.id)))
    }
  }

  const handleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleApprove = async (id: string) => {
    try {
      setActionLoading(id)
      const response = await api.approveTestimonial(id)
      setTestimonials((prev) => prev.map((t) => (t.id === id ? response.data : t)))
      toast.success('Testimonial approved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve testimonial')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (id: string) => {
    try {
      setActionLoading(id)
      const response = await api.rejectTestimonial(id)
      setTestimonials((prev) => prev.map((t) => (t.id === id ? response.data : t)))
      toast.success('Testimonial rejected')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject testimonial')
    } finally {
      setActionLoading(null)
    }
  }

  const handleFavorite = async (id: string) => {
    try {
      setActionLoading(id)
      const response = await api.favoriteTestimonial(id)
      setTestimonials((prev) => prev.map((t) => (t.id === id ? response.data : t)))
      toast.success(response.data.isFavorite ? 'Added to favorites' : 'Removed from favorites')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update favorite')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this testimonial? This action cannot be undone.'
      )
    ) {
      return
    }
    try {
      setActionLoading(id)
      await api.deleteTestimonial(id)
      setTestimonials((prev) => prev.filter((t) => t.id !== id))
      setSelected((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setTotal((prev) => prev - 1)
      toast.success('Testimonial deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete testimonial')
    } finally {
      setActionLoading(null)
    }
  }

  const handleBulkApprove = async () => {
    try {
      setActionLoading('bulk')
      const ids = Array.from(selected)
      const response = await api.bulkApproveTestimonials(ids)
      toast.success(`${response.data.updated} testimonial(s) approved`)
      setSelected(new Set())
      void fetchTestimonials()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to bulk approve')
    } finally {
      setActionLoading(null)
    }
  }

  const handleBulkReject = async () => {
    try {
      setActionLoading('bulk')
      const ids = Array.from(selected)
      const response = await api.bulkRejectTestimonials(ids)
      toast.success(`${response.data.updated} testimonial(s) rejected`)
      setSelected(new Set())
      void fetchTestimonials()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to bulk reject')
    } finally {
      setActionLoading(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <AppLayout title="Testimonials">
      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={styles.searchInput}>
          <Input
            placeholder="Search by author or content..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.statusFilter}>
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
        <button
          type="button"
          className={`${styles.favoriteToggle} ${favoriteOnly ? styles.favoriteToggleActive : ''}`}
          onClick={() => setFavoriteOnly(!favoriteOnly)}
        >
          &#9733; Favorites
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selected.size} selected</span>
          <div className={styles.bulkActions}>
            <Button
              size="sm"
              variant="primary"
              onClick={() => void handleBulkApprove()}
              loading={actionLoading === 'bulk'}
            >
              Approve All
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => void handleBulkReject()}
              loading={actionLoading === 'bulk'}
            >
              Reject All
            </Button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading State */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner size="lg" />
          <p>Loading testimonials...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && testimonials.length === 0 && (
        <Card>
          <CardBody>
            <div className={styles.emptyState}>
              <div className={styles.emptyIconCircle}>
                <span className={styles.emptyIconEmoji}>&#128172;</span>
              </div>
              <h3 className={styles.emptyTitle}>
                {searchDebounced || statusFilter || favoriteOnly
                  ? 'No matching testimonials'
                  : 'No testimonials yet'}
              </h3>
              <p className={styles.emptyDescription}>
                {searchDebounced || statusFilter || favoriteOnly
                  ? 'Try adjusting your filters to see more results.'
                  : 'Create a collection page and share it with your customers to start receiving testimonials. Once they submit feedback, it will appear here for you to review and approve.'}
              </p>
              {!searchDebounced && !statusFilter && !favoriteOnly && (
                <div className={styles.emptyActions}>
                  <Link to="/collection-pages">
                    <Button variant="primary">Create Collection Page</Button>
                  </Link>
                  <a
                    href="https://docs.proofpulse.io/getting-started"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost">View Guide</Button>
                  </a>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Testimonials Table */}
      {!isLoading && !error && testimonials.length > 0 && (
        <>
          <div className={styles.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selected.size === testimonials.length && testimonials.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableHeader>
                  <TableHeader>Author</TableHeader>
                  <TableHeader>Content</TableHeader>
                  <TableHeader>Rating</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Date</TableHeader>
                  <TableHeader style={{ width: '140px' }}>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {testimonials.map((testimonial) => (
                  <TableRow key={testimonial.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selected.has(testimonial.id)}
                        onChange={() => handleSelect(testimonial.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={styles.authorCell}>
                        <span className={styles.authorName}>{testimonial.authorName}</span>
                        {testimonial.authorEmail && (
                          <span className={styles.authorEmail}>{testimonial.authorEmail}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.contentPreview}>{testimonial.content}</div>
                    </TableCell>
                    <TableCell>
                      <StarRating rating={testimonial.rating} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[testimonial.status]}>
                        {testimonial.status.charAt(0).toUpperCase() + testimonial.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={styles.dateCell}>{formatDate(testimonial.createdAt)}</span>
                    </TableCell>
                    <TableCell>
                      <div className={styles.actions}>
                        {testimonial.status !== 'approved' && (
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => void handleApprove(testimonial.id)}
                            disabled={actionLoading === testimonial.id}
                            title="Approve"
                          >
                            &#10003;
                          </button>
                        )}
                        {testimonial.status !== 'rejected' && (
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => void handleReject(testimonial.id)}
                            disabled={actionLoading === testimonial.id}
                            title="Reject"
                          >
                            &#10007;
                          </button>
                        )}
                        <button
                          type="button"
                          className={`${styles.actionBtn} ${styles.favoriteBtn} ${testimonial.isFavorite ? styles.favoriteBtnActive : ''}`}
                          onClick={() => void handleFavorite(testimonial.id)}
                          disabled={actionLoading === testimonial.id}
                          title={
                            testimonial.isFavorite ? 'Remove from favorites' : 'Add to favorites'
                          }
                        >
                          &#9733;
                        </button>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          onClick={() => void handleDelete(testimonial.id)}
                          disabled={actionLoading === testimonial.id}
                          title="Delete"
                        >
                          &#128465;
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className={styles.pagination}>
            <span className={styles.pageInfo}>
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </span>
            <div className={styles.pageButtons}>
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  )
}
